import { OnrampApiError } from '../client.js';
import { parseExchangeAmount, validateExchangeAmount } from './exchange-amount.js';
import { exchangeBack, exchangeStatusScreen, exchangeTarget, } from './exchange-state.js';
export function createExchangeController(client, surface, locale, createId = defaultId) {
    let state = {
        screen: 'exchanges',
        operationId: null,
        authorization: null,
        balances: [],
        balance: null,
        route: null,
        amount: '',
        review: null,
        status: null,
        submitted: false,
        busy: false,
        error: null,
    };
    let idempotencyKey = null;
    let generation = 0;
    let refreshingGeneration = null;
    let disposed = false;
    const listeners = new Set();
    const update = (patch) => {
        if (disposed)
            return;
        state = { ...state, ...patch };
        for (const listener of listeners)
            listener();
    };
    const provider = () => {
        if (!client.exchange)
            throw new Error('exchange_unavailable');
        return client.exchange;
    };
    const applyStatus = (status) => {
        if (state.submitted &&
            ['pending', 'connected', 'rejected', 'expired'].includes(status.status)) {
            update({ status: { status: 'submission_unknown', challenge: null }, screen: 'status' });
            return;
        }
        const submitted = !['pending', 'connected', 'rejected', 'expired'].includes(status.status);
        update({
            status,
            submitted: state.submitted || submitted,
            screen: exchangeStatusScreen(status.status),
        });
    };
    const reconnect = (error) => {
        if (state.submitted ||
            !(error instanceof OnrampApiError) ||
            error.status !== 422 ||
            !['exchange_connection_disconnected', 'exchange_authorization_expired'].includes(error.reason ?? ''))
            return false;
        update({
            screen: 'exchange_auth',
            authorization: null,
            balances: [],
            balance: null,
            route: null,
            review: null,
            error: 'disconnected',
            status: { status: 'expired', challenge: null },
        });
        return true;
    };
    const run = async (action, error = 'unavailable') => {
        if (disposed || state.busy)
            return;
        // User actions supersede any older status/account read still in flight.
        generation += 1;
        const current = generation;
        update({ busy: true, error: null });
        try {
            await action();
        }
        catch (failure) {
            if (current === generation && !reconnect(failure))
                update({ error });
        }
        finally {
            if (current === generation)
                update({ busy: false });
        }
    };
    const refresh = async () => {
        if (disposed || state.busy || (state.screen === 'exchanges' && !state.submitted))
            return;
        const current = generation;
        if (refreshingGeneration === current)
            return;
        refreshingGeneration = current;
        try {
            const id = state.operationId;
            if (!id)
                return;
            const status = await provider().status(id);
            if (current !== generation)
                return;
            update({ error: null });
            applyStatus(status);
            if (status.status === 'connected' && !state.submitted) {
                const balances = await provider().accounts(id);
                if (current !== generation)
                    return;
                update({ balances });
            }
        }
        catch (failure) {
            if (current === generation && !reconnect(failure))
                update({ error: 'unavailable' });
        }
        finally {
            if (refreshingGeneration === current)
                refreshingGeneration = null;
        }
    };
    return {
        getSnapshot: () => state,
        subscribe(listener) {
            listeners.add(listener);
            return () => {
                listeners.delete(listener);
            };
        },
        async connect() {
            if (disposed || state.submitted || state.busy)
                return null;
            let authorization = null;
            await run(async () => {
                const current = generation;
                idempotencyKey ??= createId();
                authorization = await provider().authorize({
                    idempotencyKey,
                    ...(state.operationId ? { operationId: state.operationId } : {}),
                    surface,
                    locale,
                });
                if (current !== generation) {
                    authorization = null;
                    return;
                }
                update({
                    authorization,
                    operationId: authorization.operationId,
                    screen: exchangeTarget('connector_selected'),
                    status: { status: 'pending', challenge: null },
                });
            });
            return authorization;
        },
        resume(id) {
            update({ operationId: id, screen: 'status' });
            return refresh();
        },
        refresh,
        selectBalance(balanceId) {
            if (state.submitted)
                return;
            const balance = state.balances.find((candidate) => candidate.id === balanceId);
            if (balance)
                update({ balance, route: null, review: null, screen: exchangeTarget('balance_selected') });
        },
        selectRoute(routeId) {
            if (state.submitted)
                return;
            const route = state.balance?.routes.find((candidate) => candidate.id === routeId);
            if (route)
                update({
                    route,
                    amount: '',
                    review: null,
                    error: null,
                    screen: exchangeTarget('source_selected'),
                });
        },
        setAmount(amount) {
            if (!state.submitted)
                update({ amount, review: null, error: null });
        },
        prepare() {
            const { route, balance, operationId, amount } = state;
            if (!route || !balance || !operationId || state.submitted)
                return Promise.resolve();
            let units;
            try {
                units = parseExchangeAmount(amount, route.decimals);
            }
            catch {
                update({ error: 'invalid' });
                return Promise.resolve();
            }
            const error = validateExchangeAmount({ ...route, units });
            if (error) {
                update({ error });
                return Promise.resolve();
            }
            return run(async () => {
                const review = await provider().prepare(operationId, {
                    balanceId: balance.id,
                    routeId: route.id,
                    units,
                });
                update({ review, screen: exchangeTarget('amount_confirmed') });
            });
        },
        submit() {
            const { review, operationId, submitted } = state;
            if (!review || !operationId || submitted || state.busy)
                return Promise.resolve();
            if (Date.parse(review.expiresAt) <= Date.now()) {
                update({ review: null, screen: 'amount', error: 'review_changed' });
                return Promise.resolve();
            }
            return run(async () => {
                try {
                    applyStatus(await provider().submit(operationId, review.reviewId));
                }
                catch (error) {
                    if (reconnect(error))
                        return;
                    if (error instanceof OnrampApiError &&
                        error.status === 422 &&
                        ['exchange_review_changed', 'exchange_review_expired'].includes(error.reason ?? '')) {
                        update({ review: null, screen: 'amount', error: 'review_changed' });
                        return;
                    }
                    // A failed response does not prove rejection or permit another POST.
                    update({
                        submitted: true,
                        screen: 'status',
                        status: { status: 'submission_unknown', challenge: null },
                    });
                    const status = await provider().status(operationId);
                    if (!['pending', 'connected', 'expired', 'rejected'].includes(status.status))
                        applyStatus(status);
                }
            });
        },
        confirm(code) {
            const { operationId, status } = state;
            if (!operationId ||
                status?.status !== 'action_required' ||
                !status.challenge ||
                Date.parse(status.challenge.expiresAt) <= Date.now() ||
                !status.challenge.codeLengths.includes(code.length) ||
                !/^\d+$/.test(code))
                return Promise.resolve();
            return run(async () => {
                try {
                    applyStatus(await provider().confirm(operationId, code));
                }
                catch {
                    applyStatus(await provider().status(operationId));
                    update({ error: 'verification_rejected' });
                }
            }, 'verification_rejected');
        },
        resend() {
            const { operationId, status } = state;
            if (!operationId ||
                !status?.challenge ||
                Date.parse(status.challenge.resendAvailableAt) > Date.now() ||
                Date.parse(status.challenge.expiresAt) <= Date.now())
                return Promise.resolve();
            return run(async () => applyStatus(await provider().resend(operationId)));
        },
        back() {
            const screen = exchangeBack(state.screen, state.submitted);
            if (!state.submitted && (screen === 'exchanges' || screen === 'methods')) {
                generation += 1;
                update({ busy: false, authorization: null });
            }
            if (screen !== 'methods')
                update({ screen, error: null, review: null });
            return screen;
        },
        dispose() {
            disposed = true;
            generation += 1;
            state = {
                ...state,
                authorization: null,
                balances: [],
                balance: null,
                route: null,
                review: null,
                amount: '',
            };
            listeners.clear();
        },
    };
}
function defaultId() {
    return crypto.randomUUID();
}
